import { ChevronDown, ChevronRight, Info, Split } from "lucide-react";

import type {
  CrossScreenMode,
  OtherStation,
  StationRole,
} from "~/components/stations/station-edit-types";
import {
  getStationRoleDescription,
  isCrossScreenMode,
  isStationRole,
} from "~/components/stations/station-edit-types";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { parseEnumValue } from "~/lib/parse-enum-value";

interface KdsCrossScreenSectionProps {
  crossScreenMode: CrossScreenMode;
  setCrossScreenMode: (mode: CrossScreenMode) => void;
  nextStationId: string | null;
  setNextStationId: (id: string | null) => void;
  stationRole: StationRole;
  setStationRole: (role: StationRole) => void;
  otherStations: OtherStation[];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export function KdsCrossScreenSection({
  crossScreenMode,
  setCrossScreenMode,
  nextStationId,
  setNextStationId,
  stationRole,
  setStationRole,
  otherStations,
  isOpen,
  setIsOpen,
}: KdsCrossScreenSectionProps) {
  const handleRoleChange = (v: string) => {
    setStationRole(parseEnumValue(v, isStationRole, "prep"));
    if (v === "expo") {
      setNextStationId(null);
    }
  };

  const expoStations = otherStations.filter(
    (s) => s.crossScreenMode === "prep-expo" && s.stationRole === "expo"
  );

  return (
    <Collapsible onOpenChange={setIsOpen} open={isOpen}>
      <CollapsibleTrigger asChild>
        <div className="border-border hover:bg-accent/50 flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3">
          <div className="flex items-center gap-3">
            <Split className="text-muted-foreground h-5 w-5" />
            <div>
              <p className="font-medium">Cross-Screen Mode</p>
              <p className="text-muted-foreground text-sm">
                How multiple screens interact
              </p>
            </div>
          </div>
          {isOpen ? (
            <ChevronDown className="text-muted-foreground h-5 w-5" />
          ) : (
            <ChevronRight className="text-muted-foreground h-5 w-5" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-border bg-accent/30 mt-2 space-y-4 rounded-xl border p-4">
          <div className="flex flex-col gap-2">
            <Label>Mode</Label>
            <Select
              onValueChange={(v) =>
                setCrossScreenMode(
                  parseEnumValue(v, isCrossScreenMode, "independent")
                )
              }
              value={crossScreenMode}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="independent">
                  Independent - Screens operate separately
                </SelectItem>
                <SelectItem value="multi-clear">
                  Multi-Clear - Bump clears across screens
                </SelectItem>
                <SelectItem value="send-to-next">
                  Send to Next - Bump sends to next station
                </SelectItem>
                <SelectItem value="prep-expo">
                  Prep/Expo - Separate prep and expo flow
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {crossScreenMode === "send-to-next" && (
            <CrossScreenSendToNext
              nextStationId={nextStationId}
              otherStations={otherStations}
              setNextStationId={setNextStationId}
            />
          )}

          {crossScreenMode === "prep-expo" && (
            <CrossScreenPrepExpo
              expoStations={expoStations}
              nextStationId={nextStationId}
              setNextStationId={setNextStationId}
              setStationRole={handleRoleChange}
              stationRole={stationRole}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function CrossScreenSendToNext({
  nextStationId,
  setNextStationId,
  otherStations,
}: {
  nextStationId: string | null;
  setNextStationId: (id: string | null) => void;
  otherStations: OtherStation[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label>Next Station</Label>
      <Select
        onValueChange={(v) => setNextStationId(v || null)}
        value={nextStationId ?? ""}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select next station..." />
        </SelectTrigger>
        <SelectContent>
          {otherStations.map((s) => (
            <SelectItem key={s._id} value={s._id}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">
        When an order is bumped, it will be sent to this station
      </p>
    </div>
  );
}

function CrossScreenPrepExpo({
  stationRole,
  setStationRole,
  nextStationId,
  setNextStationId,
  expoStations,
}: {
  stationRole: StationRole;
  setStationRole: (v: string) => void;
  nextStationId: string | null;
  setNextStationId: (id: string | null) => void;
  expoStations: OtherStation[];
}) {
  return (
    <>
      <div className="flex flex-col gap-2">
        <Label>Station Role</Label>
        <Select onValueChange={setStationRole} value={stationRole ?? ""}>
          <SelectTrigger>
            <SelectValue placeholder="Select role..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="prep">
              Prep Station - Works on filtered items
            </SelectItem>
            <SelectItem value="expo">
              Expo Station - Receives complete orders
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          {getStationRoleDescription(stationRole)}
        </p>
      </div>

      {stationRole === "prep" && (
        <PrepStationExpoSelector
          expoStations={expoStations}
          nextStationId={nextStationId}
          setNextStationId={setNextStationId}
        />
      )}
    </>
  );
}

function PrepStationExpoSelector({
  expoStations,
  nextStationId,
  setNextStationId,
}: {
  expoStations: OtherStation[];
  nextStationId: string | null;
  setNextStationId: (id: string | null) => void;
}) {
  if (expoStations.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        <Label>Expo Station</Label>
        <div className="border-info/30 bg-info/10 dark:border-info/30 dark:bg-info/10 flex items-start gap-3 rounded-lg border p-3">
          <Info className="text-info dark:text-info mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex flex-col gap-1">
            <p className="text-info dark:text-info/80 text-sm font-medium">
              No expo station configured yet
            </p>
            <p className="text-info dark:text-info/70 text-xs">
              Create an expo station first, then return here to link this prep
              station to it. Set up flow: Expo station &rarr; Prep station(s).
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Label>Expo Station</Label>
      <Select
        onValueChange={(v) => setNextStationId(v || null)}
        value={nextStationId ?? ""}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select expo station..." />
        </SelectTrigger>
        <SelectContent>
          {expoStations.map((s) => (
            <SelectItem key={s._id} value={s._id}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">
        The expo station will receive the order when all prep stations are done
      </p>
    </div>
  );
}
